import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { BiChevronLeft } from "react-icons/bi";

export interface FoodBookingSkeletonProps {
  cardBg?: string;
  itemBg?: string;
  borderColor?: string;
  spinnerColor?: string;
  startColor?: string;
  endColor?: string;
}

function FoodBookingSkeleton({
  cardBg = "white",
  itemBg = "#F4F4F4",
  borderColor = "gray.200",
  spinnerColor = "blue.500",
  startColor,
  endColor,
}: FoodBookingSkeletonProps) {
  const skeletonProps = { startColor, endColor };

  return (
    <Box py={{ base: 6 }}>
      {/* Header: Back button + Timer */}
      <Flex justify="space-between" align="center" my="2rem">
        <Button
          leftIcon={<BiChevronLeft size={20} />}
          variant="ghost"
          isLoading
          width="120px"
        >
          <Skeleton width="80px" height="20px" {...skeletonProps} />
        </Button>

        <Skeleton
          width="180px"
          height="28px"
          borderRadius="full"
          {...skeletonProps}
        />
      </Flex>

      <Grid
        templateColumns={{
          base: "minmax(0, 1fr)",
          lg: "minmax(0, 2fr) minmax(0, 1fr)",
        }}
        gap={6}
      >
        {/* Left Side - Food Selection */}
        <GridItem>
          {/* Hero Banner */}
          <Skeleton
            height="200px"
            borderRadius="xl"
            mb={6}
            {...skeletonProps}
          />

          {/* Main Food Card */}
          <Box bg={itemBg} p={6} borderRadius="xl">
            <Stack spacing={4}>
              <Center>
                <Skeleton height="32px" width="60%" {...skeletonProps} />
              </Center>
              <Center>
                <SkeletonText
                  noOfLines={2}
                  width="80%"
                  spacing={3}
                  {...skeletonProps}
                />
              </Center>

              {/* Tabs Skeleton */}
              <Box bg={cardBg} borderRadius="11px" px="10px" py={2}>
                <Flex gap={6}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton
                      key={i}
                      height="40px"
                      borderRadius="md"
                      {...skeletonProps}
                    />
                  ))}
                </Flex>
              </Box>

              {/* Snack Items Grid */}
              <Grid
                templateColumns={{
                  base: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                  xl: "repeat(5, minmax(0, 1fr))",
                  "2xl": "repeat(6, minmax(0, 1fr))",
                }}
                gap={4}
              >
                {[...Array(12)].map((_, i) => (
                  <Flex
                    key={i}
                    direction="column"
                    gap={3}
                    p={3}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    bg={cardBg}
                  >
                    <Skeleton
                      w="full"
                      aspectRatio={1}
                      borderRadius="md"
                      {...skeletonProps}
                    />
                    <Skeleton height="16px" width="80%" {...skeletonProps} />
                    <Skeleton height="12px" width="60%" {...skeletonProps} />
                    <Flex justify="space-between" align="center" gap={2}>
                      <Skeleton height="18px" width="50px" {...skeletonProps} />
                      <Skeleton
                        height="32px"
                        width="56px"
                        borderRadius="full"
                        {...skeletonProps}
                      />
                    </Flex>
                  </Flex>
                ))}
              </Grid>

              {/* Infinite Scroll Loader */}
              <Center py={8}>
                <Spinner size="lg" color={spinnerColor} thickness="4px" />
              </Center>

              <Center>
                <Skeleton height="14px" width="200px" {...skeletonProps} />
              </Center>
            </Stack>
          </Box>
        </GridItem>

        {/* Right Side - Booking Summary Sidebar */}
        <GridItem>
          <Box
            bg={cardBg}
            borderRadius="xl"
            boxShadow="lg"
            overflow="hidden"
            minHeight="500px"
          >
            {/* Header */}
            <Box p={6}>
              <Skeleton height="28px" width="70%" {...skeletonProps} />
              <Skeleton height="20px" width="50%" mt={2} {...skeletonProps} />
            </Box>

            {/* Movie Info */}
            <Box p={6} borderBottomWidth="1px" borderColor={borderColor}>
              <Skeleton height="24px" width="full" mb={3} {...skeletonProps} />
              <Skeleton height="18px" width="80%" {...skeletonProps} />
              <Skeleton height="18px" width="60%" mt={2} {...skeletonProps} />
            </Box>

            {/* Cart Items */}
            <Box p={6}>
              <Skeleton height="20px" width="120px" mb={4} {...skeletonProps} />

              <Stack spacing={4}>
                {[...Array(3)].map((_, i) => (
                  <Flex key={i} justify="space-between" align="center">
                    <Flex align="center" gap={3}>
                      <SkeletonCircle size="10" {...skeletonProps} />
                      <Box>
                        <Skeleton
                          height="18px"
                          width="140px"
                          {...skeletonProps}
                        />
                        <Skeleton
                          height="14px"
                          width="80px"
                          mt={1}
                          {...skeletonProps}
                        />
                      </Box>
                    </Flex>
                    <Skeleton height="18px" width="60px" {...skeletonProps} />
                  </Flex>
                ))}
              </Stack>

              {/* Total */}
              <Flex justify="space-between" mt={6} fontWeight="bold">
                <Skeleton height="24px" width="80px" {...skeletonProps} />
                <Skeleton height="28px" width="100px" {...skeletonProps} />
              </Flex>

              {/* Proceed Button */}
              <Skeleton
                mt={8}
                height="50px"
                borderRadius="full"
                {...skeletonProps}
              />
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
}

export default FoodBookingSkeleton;
